import React, { FC } from 'react';

import {
	ChevronRight,
	EvBodyDefaultLink,
	EvFlexColumn,
	EvFlexRow,
	EvLift,
	EvSvgIcon,
	EvTag,
	EvTitle,
	TAG_STATUS
} from '@evinced-private/ui-common';

import { EvLinkLocal } from 'src/common/components/ev-link-local/EvLinkLocal';

import { DrilldownState } from './helpers/drilldownLinkHelper';
import { TREND_TAG_KIND } from './constants';
import { StatusTag, TrendTag } from './types';

interface BaseCardProps {
	id: string;
	title: string;
	className: string;
	infoContent: string;
	ariaLabel: string;
}

interface ScoreCardProps extends BaseCardProps {
	type: 'score';
	score: number | null;
	maxScore?: number;
	statusTag: StatusTag;
}

interface MetricCardProps extends BaseCardProps {
	type: 'metric';
	value: string;
	trendTag: TrendTag;
	trendAriaLabel: string;
	drilldownLink: {
		text: string;
		url: string;
		ariaLabel: string;
		state?: DrilldownState;
	};
}

type MetricContentProps = Pick<
	MetricCardProps,
	'value' | 'trendTag' | 'trendAriaLabel' | 'drilldownLink'
> & {
	ariaLabel: string;
};

type AccessibilitySummaryCardProps = ScoreCardProps | MetricCardProps;

export const AccessibilitySummaryCard: FC<AccessibilitySummaryCardProps> = (props) => {
	const { id, title, className, infoContent, ariaLabel, type } = props;
	const isScoreType = type === 'score';

	return (
		<div className={`summary-card ${className}`} role="group" aria-labelledby={`${id}-title`}>
			<div className="card-header">
				<EvTitle
					id={`${id}-title`}
					titleText={title}
					headingLevel={3}
					className="card-title"
					MoreInfoContent={<div>{infoContent}</div>}
				/>
			</div>
			<div className="card-content">
				{isScoreType ? (
					<>
						<div className="score-display">
							<span className="score-value" aria-label={ariaLabel}>
								{props.score === null ? '0' : props.score}
							</span>
							{props.score !== null && props.maxScore && (
								<span className="score-denominator" aria-hidden="true">
									/{props.maxScore}
								</span>
							)}
						</div>
						<EvTag
							status={props.statusTag.status}
							text={props.statusTag.text}
							aria-label={`Status: ${props.statusTag.text}`}
						/>
					</>
				) : (
					<MetricContent ariaLabel={ariaLabel} {...props} />
				)}
			</div>
		</div>
	);
};

const MetricContent: FC<MetricContentProps> = ({
	ariaLabel,
	value,
	trendTag,
	trendAriaLabel,
	drilldownLink
}) => {
	const drilldownLinkProps = {
		url: drilldownLink.url,
		ariaLabel: drilldownLink.ariaLabel,
		state: drilldownLink.state
	};

	return (
		<EvFlexColumn justify="SPACE_BETWEEN" align="CENTER" flex="1">
			<EvFlexRow justify="CENTER" align="CENTER">
				<div className="metric-value-container">
					<span className="metric-value" aria-label={ariaLabel}>
						{value}
					</span>
					{trendTag.kind === TREND_TAG_KIND.LIFT ? (
						<EvFlexRow role="img" ariaLabel={trendAriaLabel}>
							<EvLift
								liftNumber={trendTag.liftNumber}
								small
								success={trendTag.success}
								ariaHidden
							/>
						</EvFlexRow>
					) : (
						<EvTag status={TAG_STATUS.INFO} text={trendTag.text} aria-label={trendAriaLabel} />
					)}
				</div>
			</EvFlexRow>
			<EvLinkLocal
				{...drilldownLinkProps}
				customLink={
					<div className="metric-drilldown-link">
						<EvBodyDefaultLink>{drilldownLink.text}</EvBodyDefaultLink>
						<EvSvgIcon icon={ChevronRight} isSmall />
					</div>
				}
			/>
		</EvFlexColumn>
	);
};
